import {
  FETCH_ASSET_TEMPLATES,
  FETCH_ASSET_GROUPS,
  FETCH_ASSET_ITEMS,
  FETCH_TASK_OCCURRENCES,
  FETCH_ASSET_SITE_INVENTORIES,
  SELECT_ASSET_NODE,
  TOGGLE_TREE_NODE,
  SET_ASSET_SEARCH_TERM,
  EXPAND_ALL_NODES,
  COLLAPSE_ALL_NODES,
  OPEN_ADD_TEMPLATE_DRAWER,
  CLOSE_ADD_TEMPLATE_DRAWER,
  OPEN_NODE_ACTION_DRAWER,
  CLOSE_NODE_ACTION_DRAWER,
  DESELECT_TEMPLATE,
  RESET_ASSET_VIEW,
  GET_ASSET_TEMPLATE,
  GET_ASSET_GROUP_DETAIL,
  GET_ASSET_ITEM_DETAIL,
  SELECT_SIDEBAR_ITEM,
  DESELECT_SIDEBAR_ITEM,
} from './actions';
import { getAllNodeIds, buildTreeFromTemplate } from './helpers';

const normalizeList = (response) => {
  if (Array.isArray(response)) return response;
  return response?.results || response?.data || [];
};

const initialState = {
  templates: [],
  groups: [],
  items: [],
  taskOccurrences: [],
  siteInventories: [],
  selectedTemplateId: null,
  tree: null,
  selectedNodeId: null,
  expandedNodeIds: [],
  searchTerm: '',
  loading: false,
  error: null,
  selectedSidebarItem: null,
  addTemplateDrawerOpen: false,
  nodeActionDrawer: {
    open: false,
    mode: null,
    node: null,
    breadcrumb: [],
  },
};

export function assets(state = initialState, action) {
  switch (action.type) {
    case RESET_ASSET_VIEW:
      return { ...initialState };

    case `${FETCH_ASSET_TEMPLATES}_SUCCESS`:
      return { ...state, templates: normalizeList(action.response) };

    case `${FETCH_ASSET_GROUPS}_SUCCESS`:
      return { ...state, groups: normalizeList(action.response) };

    case `${FETCH_ASSET_ITEMS}_SUCCESS`:
      return { ...state, items: normalizeList(action.response) };

    case `${FETCH_TASK_OCCURRENCES}_SUCCESS`:
      return { ...state, taskOccurrences: normalizeList(action.response) };

    case `${FETCH_ASSET_SITE_INVENTORIES}_SUCCESS`:
      return { ...state, siteInventories: normalizeList(action.response) };

    case `${GET_ASSET_TEMPLATE}_REQUEST`:
      return { ...state, loading: true, error: null };

    case `${GET_ASSET_TEMPLATE}_SUCCESS`: {
      const tree = buildTreeFromTemplate(action.response);
      return {
        ...state,
        loading: false,
        error: null,
        tree,
        selectedTemplateId: action.response?.id || null,
        selectedNodeId: tree?.id || null,
        expandedNodeIds: tree ? [tree.id] : [],
        selectedSidebarItem: null,
        searchTerm: '',
      };
    }

    case `${GET_ASSET_TEMPLATE}_FAILURE`:
      return { ...state, loading: false, error: action.error || 'Failed to load template' };

    case `${GET_ASSET_GROUP_DETAIL}_SUCCESS`: {
      const current = state.selectedSidebarItem;
      const responseId = action.response?.id ?? action.meta?.id;
      if (!current || current.itemType !== 'group' || current.id !== responseId) {
        return state;
      }
      return {
        ...state,
        selectedSidebarItem: { ...current, data: action.response },
      };
    }

    case `${GET_ASSET_ITEM_DETAIL}_SUCCESS`: {
      const current = state.selectedSidebarItem;
      const responseId = action.response?.id ?? action.meta?.id;
      if (!current || current.itemType !== 'item' || current.id !== responseId) {
        return state;
      }
      return {
        ...state,
        selectedSidebarItem: { ...current, data: action.response },
      };
    }

    case DESELECT_TEMPLATE:
      return {
        ...state,
        tree: null,
        selectedTemplateId: null,
        selectedNodeId: null,
        expandedNodeIds: [],
        searchTerm: '',
      };

    case SELECT_ASSET_NODE:
      return { ...state, selectedNodeId: action.nodeId };

    case TOGGLE_TREE_NODE: {
      const isExpanded = state.expandedNodeIds.includes(action.nodeId);
      return {
        ...state,
        expandedNodeIds: isExpanded
          ? state.expandedNodeIds.filter((id) => id !== action.nodeId)
          : [...state.expandedNodeIds, action.nodeId],
      };
    }

    case SET_ASSET_SEARCH_TERM:
      return { ...state, searchTerm: action.searchTerm };

    case EXPAND_ALL_NODES:
      return { ...state, expandedNodeIds: getAllNodeIds(state.tree) };

    case COLLAPSE_ALL_NODES:
      return { ...state, expandedNodeIds: [] };

    case OPEN_ADD_TEMPLATE_DRAWER:
      return { ...state, addTemplateDrawerOpen: true };

    case CLOSE_ADD_TEMPLATE_DRAWER:
      return { ...state, addTemplateDrawerOpen: false };

    case OPEN_NODE_ACTION_DRAWER:
      return {
        ...state,
        nodeActionDrawer: {
          open: true,
          mode: action.mode,
          node: action.node,
          breadcrumb: action.breadcrumb,
          initialItemType: action.initialItemType || null,
          editData: action.editData || null,
        },
      };

    case CLOSE_NODE_ACTION_DRAWER:
      return {
        ...state,
        nodeActionDrawer: { ...initialState.nodeActionDrawer },
      };

    case SELECT_SIDEBAR_ITEM:
      return {
        ...state,
        selectedSidebarItem: {
          itemType: action.itemType,
          id: action.id,
          data: action.data,
        },
        selectedTemplateId: null,
        tree: null,
        selectedNodeId: null,
        expandedNodeIds: [],
      };

    case DESELECT_SIDEBAR_ITEM:
      return {
        ...state,
        selectedSidebarItem: null,
      };

    default:
      return state;
  }
}

// Selectors
export const selectAssetTemplates = (state) => state.assets.templates || [];
export const selectAssetGroups = (state) => state.assets.groups || [];
export const selectAssetItems = (state) => state.assets.items || [];
export const selectAssetTaskOccurrences = (state) => state.assets.taskOccurrences || [];
export const selectAssetSiteInventories = (state) => state.assets.siteInventories || [];
export const selectAssetTree = (state) => state.assets.tree || null;
export const selectSelectedTemplateId = (state) => state.assets.selectedTemplateId;
export const selectSelectedNodeId = (state) => state.assets.selectedNodeId;
export const selectExpandedNodeIds = (state) => state.assets.expandedNodeIds;
export const selectAssetSearchTerm = (state) => state.assets.searchTerm;
export const selectAssetLoading = (state) => state.assets.loading;
export const selectAssetError = (state) => state.assets.error;
export const selectAddTemplateDrawerOpen = (state) => state.assets.addTemplateDrawerOpen;
export const selectNodeActionDrawer = (state) => state.assets.nodeActionDrawer;
export const selectSelectedSidebarItem = (state) => state.assets.selectedSidebarItem;
